import React, { useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase-config';

export default function Login({ error: externalError }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(externalError || '');

  // Log login event to backend for audit trail + email alerts
  const logLogin = (success, reason) => {
    try {
      const fns = getFunctions();
      const authApi = httpsCallable(fns, 'authApi');
      authApi({ action: 'logUserLogin', eventType: 'login', email, success, reason, platform: 'merchant_web' });
    } catch (_) { /* non-blocking */ }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await signInWithEmailAndPassword(auth, email, password);
      logLogin(true, null);
    } catch (err) {
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password');
        logLogin(false, 'invalid_credentials');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
        logLogin(false, 'too_many_requests');
      } else {
        setError('Login failed. Please try again.');
        logLogin(false, err.code || 'unknown');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary">
            KwikX<span className="text-accent">.</span>
          </h1>
          <p className="text-text-secondary mt-2">Merchant Desk</p>
        </div>

        {/* Login Card */}
        <div className="bg-surface rounded-xl shadow-card p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-text-main mb-6">Sign in to your account</h2>

          {(error || externalError) && (
            <div className="mb-4 p-3 bg-status-red-bg text-status-red text-sm rounded-lg">
              {error || externalError}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-main mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                placeholder="merchant@example.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-main mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-white rounded-lg font-semibold text-sm hover:bg-primary-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-text-secondary">
            Contact admin to create a merchant account
          </p>
        </div>
      </div>
    </div>
  );
}
